"use client"

import type React from "react"
import { memo } from "react"
import type { SyncOption } from "../../../types/choose-sync"
import { useSyncOptionCard } from "../../../hooks/useSyncOptionCard"
import DirectionArrow from "./DirectionArrow"

interface SyncOptionCardProps {
  option: SyncOption
  isSelected: boolean
  onSelect: (optionId: string) => void
}

const SyncOptionCard: React.FC<SyncOptionCardProps> = memo(({ option, isSelected, onSelect }) => {
  const { isDisabled, syncTypeLabel, cardClassName, ariaLabel, handleClick, handleKeyDown, handleRadioChange } =
    useSyncOptionCard({ option, isSelected, onSelect })

  return (
    <div
      className={cardClassName}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-pressed={isSelected}
      aria-disabled={isDisabled}
      aria-describedby={`${option.id}-description`}
    >
      <div className="option-radio">
        <input
          type="radio"
          name="syncOptions"
          checked={isSelected}
          onChange={handleRadioChange}
          className="radio-input"
          aria-label={ariaLabel}
          disabled={isDisabled}
        />
      </div>

      <div className="option-icon" aria-hidden="true">
        {option.icon}
      </div>

      <div className="option-content">
        <div className="option-title">
          <span className="source-info">
            {option.source_system} {option.source_object}
          </span>
          <DirectionArrow direction={option.supported_directions} />
          <span className="target-info">
            {option.target_system} {option.target_object}
          </span>
        </div>

        <div className="option-description" id={`${option.id}-description`}>
          {option.description}
        </div>

        <div className="option-meta">
          <span className={`sync-type ${option.supported_directions}`}>{syncTypeLabel}</span>
          {isDisabled && (
            <span className="disabled-badge" aria-label="Currently unavailable">
              Coming Soon
            </span>
          )}
        </div>
      </div>
    </div>
  )
})

SyncOptionCard.displayName = "SyncOptionCard"

export default SyncOptionCard
